import { useAddRemoveCustomerTagMutation } from 'api/user-domain/user-domain';
import { useAddCustomerTagsForm } from 'components/forms/add-tags-form/add-tags-form.hooks';
import EditTagsForm from 'components/forms/add-tags-form/edit-tags-form.component';
import { CustomersTag, useCustomersTags } from 'hooks/customer-tags.hooks';
import { t } from 'i18next';
import FormDrawer from '../form-drawer/form-drawer.component';
import { IncDrawerType } from '../inc-drawer/inc-drawer.component';

export type CustomerEditTagsDrawerDataType = { customerId?: string };

export type CustomerEditTagsDrawerProps =
  IncDrawerType<CustomerEditTagsDrawerDataType>;

const CustomerEditTagsDrawer = ({
  isOpen,
  close,
  data: drawerData,
}: CustomerEditTagsDrawerProps) => {
  const customersTags = useCustomersTags(drawerData?.customerId);
  const { form, customerTagFields } = useAddCustomerTagsForm(customersTags);

  const [addCustomerTagMutation, { isLoading: saveTagsLoading }] =
    useAddRemoveCustomerTagMutation();

  const submit = async (data: { tags: CustomersTag[] }) => {
    const newTags = data.tags.filter(
      customersTag =>
        !customersTags.customersTags.some(ct => ct.key === customersTag.key),
    );
    const updatedTags = data.tags.filter(customersTag => {
      const originalTag = customersTags.customersTags.find(
        ct => ct.key === customersTag.key,
      );
      return !originalTag ? false : customersTag.value !== originalTag?.value;
    });
    const add = [...newTags, ...updatedTags];
    const remove = customersTags.customersTags.filter(
      ct => !data.tags.some(customersTag => ct.key === customersTag.key),
    );

    if (add.length || remove.length) {
      try {
        await addCustomerTagMutation({
          userId: drawerData?.customerId,
          body: {
            add: add.length
              ? JSON.stringify(
                  add.reduce((acc, a) => {
                    acc[a.key] = a.value;
                    return acc;
                  }, {}),
                )
              : null,
            remove: remove.length
              ? JSON.stringify(
                  remove.reduce((acc, a) => {
                    acc[a.key] = a.value;
                    return acc;
                  }, {}),
                )
              : null,
          },
        });
        close();
        form.reset();
      } catch (_e) {}
    } else {
      cancel();
    }
  };

  const cancel = () => {
    close();
    form.reset({ tags: customersTags.customersTags });
  };

  return (
    <FormDrawer
      title={t('page.customers.form.addTags.addEditTags')}
      submitButtonLabel={t('page.customers.form.addTags.saveTags')}
      data-testid="AddTagsDrawer.Drawer"
      open={isOpen}
      onClose={cancel}
      onValid={submit}
      cancel={cancel}
      formMethods={form}
      isLoading={saveTagsLoading || customersTags.isLoading}
    >
      <EditTagsForm
        tags={customersTags.tags}
        customerTagFields={customerTagFields}
      />
    </FormDrawer>
  );
};

export default CustomerEditTagsDrawer;
